import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function getPendingApprovalsCount(): Promise<number> {
  const response = await axios.get(
    `${BACKEND_URL}/cars/filter-non-entrega`,
    {
      params: { approvalStatus: 'PENDING', limit: 1, page: 1 },
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response.data?.total ?? 0;
}

export async function getPendingApprovalCars(limit = 20): Promise<any[]> {
  const response = await axios.get(
    `${BACKEND_URL}/cars/filter-non-entrega`,
    {
      params: { approvalStatus: 'PENDING', limit, page: 1 },
      headers: authHeader(),
      withCredentials: true,
    }
  );
  return response.data?.cars ?? [];
}

export async function markNotificationAsRead(id: string) {
  const response = await axios.patch(
    `${BACKEND_URL}/notifications/user/${id}/read`,
    {},
    { headers: authHeader(), withCredentials: true }
  );
  return response.data;
}

/**
 * Os tipos de aviso que o backend grava (`NotificationType` do schema Prisma).
 *
 * ⚠️ at-107: esta união estava DEFASADA em 4 valores — faltavam os dois de comentário (at-90) e
 * os dois de especialidade (at-62). Não quebrava nada porque o sino só lia `title`/`message`,
 * mas qualquer decisão por tipo (ícone, cor, navegação) nasceria errada, tratando aviso real
 * como desconhecido. Ao acrescentar valor no enum do backend, acrescentar aqui no mesmo commit.
 */
export type UserNotificationType =
  | 'APPROACHING_SECTOR_DEADLINE'
  | 'APPROACHING_SHOP_DEADLINE'
  | 'SESSION_INTERRUPTED_STATUS_CHANGE'
  | 'SESSION_INTERRUPTED_COLLABORATOR_CHANGE'
  | 'SESSION_INTERRUPTED_RELEASE_REMOVED'
  | 'SESSION_INTERRUPTED_ESPECIALIDADE'
  | 'DESVINCULADO_POR_ESPECIALIDADE'
  | 'CAR_RELEASED'
  // at-46: serviço de uma esteira ficou inacabado.
  | 'SERVICO_DEIXADO_INACABADO'
  // at-90: comentário no veículo e resposta a comentário.
  | 'COMENTARIO_NOVO'
  | 'RESPOSTA_DE_COMENTARIO'
  // at-129: comentário em pedido/requisição, resposta a ele, e menção (@) em qualquer comentário.
  | 'COMENTARIO_COMPRA_NOVO'
  | 'RESPOSTA_DE_COMENTARIO_COMPRA'
  | 'MENCAO_EM_COMENTARIO'
  // previsao-pecas/at-02: chegaram peças por nota de entrada (ainda sem destinatário).
  | 'PECA_CHEGOU'
  // at-132: veículo em oficina externa passou do limite de dias sem visita.
  | 'OFICINA_EXTERNA_SEM_VISITA';

/** O item a que um aviso aponta: pedido/requisição de Compras ou pedido da Previsão de Peças. */
export type ReferenciaDoAviso = 'PEDIDO' | 'REQUISICAO' | 'PEDIDO_PREVISAO';

export interface UserNotification {
  id: string;
  userId: string;
  type: UserNotificationType;
  title: string;
  message: string;
  read: boolean;
  readAt: string | null;
  carId?: string;
  carPlate?: string;
  /**
   * at-129: o aviso de comentário em PEDIDO ou REQUISIÇÃO do Dashboard de Compras. É o que o sino
   * usa para abrir o item — `carPlate` só sabe apontar para veículo.
   */
  referenciaTipo?: ReferenciaDoAviso | null;
  referenciaId?: string | null;
  createdAt: string;
  /**
   * portal-cotacoes/at-14 (28/10/2026): o sino do topo junta os avisos de três backends — o do
   * SOAC, o do Portal de Cotações e o do módulo de Requisições. Ausente = SOAC. É o que diz a
   * qual backend marcar como lido.
   */
  origem?: 'SOAC' | 'PORTAL' | 'REQUISICOES';
  /** Tela que o clique abre, quando o aviso vem de um módulo satélite (o SOAC decide pela placa). */
  destino?: string | null;
}

export interface GetMyNotificationsResponse {
  items: UserNotification[];
  total: number;
  page: number;
  limit: number;
}

export async function getMyNotifications(page = 1, limit = 20): Promise<GetMyNotificationsResponse> {
  const response = await axios.get(
    `${BACKEND_URL}/notifications/user/mine`,
    { params: { page, limit }, headers: authHeader(), withCredentials: true }
  );

  // Compat: se backend antigo ainda retornar o array puro, normaliza.
  const data = response.data as any;
  if (data?.items && Array.isArray(data.items)) return data as GetMyNotificationsResponse;
  if (Array.isArray(data)) {
    return { items: data, total: data.length, page: 1, limit: data.length };
  }

  return { items: [], total: 0, page: 1, limit };
}

export async function getUnreadNotificationsCount(): Promise<number> {
  const response = await axios.get(
    `${BACKEND_URL}/notifications/user/unread-count`,
    { headers: authHeader(), withCredentials: true }
  );
  return response.data?.count ?? 0;
}

export async function markAllNotificationsAsRead() {
  const response = await axios.patch(
    `${BACKEND_URL}/notifications/user/read-all`,
    {},
    { headers: authHeader(), withCredentials: true }
  );
  return response.data;
}

